import { useMemo } from "react";
import { parseReasoningBlocks, type ReasoningSectionLabels } from "@/lib/reasoningParser";

interface ReasoningStructuredViewProps {
  text?: string | null;
  sectionLabels?: ReasoningSectionLabels;
  /**
   * If true, slightly tighter spacing for compact contexts.
   */
  dense?: boolean;
}

export function ReasoningStructuredView({ text, sectionLabels, dense }: ReasoningStructuredViewProps) {
  const blocks = useMemo(
    () => parseReasoningBlocks(text, { sectionLabels }),
    [text, sectionLabels],
  );

  const hasText = Boolean(text && text.trim().length > 0);

  if (!hasText) {
    return <div className="text-xs md:text-sm text-[#94A3B8]">No reasoning yet.</div>;
  }

  // If we can't detect structured blocks, fall back to a simple rich view.
  if (!blocks.length) {
    return (
      <div
        className={
          "mx-auto w-full max-w-[70ch] whitespace-pre-line text-[#CBD5E1] " +
          (dense
            ? "text-xs leading-relaxed"
            : "text-xs md:text-sm leading-6 md:leading-7")
        }
      >
        {text}
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[72ch] space-y-3">
      {blocks.map((block, idx) => {
        const isFirst = idx === 0;
        const shellPadding = dense ? "px-3 py-3" : "px-4 py-4 md:px-5";
        const contentSpacing = dense ? "mt-2 pt-2" : "mt-3 pt-3";

        return (
          <section
            key={block.number}
            className={
              "rounded-2xl border border-[#223047]/90 bg-[#0C1626]/85 shadow-[0_12px_32px_-24px_rgba(56,189,248,0.45)] " +
              shellPadding +
              (isFirst ? "" : "")
            }
          >
            <div className="flex items-start gap-3 md:gap-4">
              <span
                className={
                  "shrink-0 inline-flex min-w-[2.4rem] items-center justify-center rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-2 py-1 font-mono text-[11px] text-cyan-200"
                }
              >
                [{block.number}]
              </span>
              <div className="min-w-0 flex-1">
                <div
                  className="text-[10px] md:text-[11px] font-semibold uppercase tracking-[0.18em] text-[#8EC6E9]"
                >
                  {block.title}
                </div>
              </div>
            </div>

            <div
              className={
                "whitespace-pre-line border-t border-white/5 text-[#D7E2EE] " +
                contentSpacing +
                " " +
                (dense
                  ? "text-xs leading-relaxed"
                  : "text-xs md:text-sm leading-6 md:leading-7")
              }
            >
              {block.content || "—"}
            </div>
          </section>
        );
      })}
    </div>
  );
}

export default ReasoningStructuredView;
